import {
  gridPaths,
  invalidateBuilds,
  invalidateDataGridPathPrefix,
  invalidateExecutionRunSummaries,
  invalidateExecutionRuns,
  invalidateGridQuery,
  invalidateMetricsBatchPrefix,
  invalidateMetricsPrefix,
  invalidateResultsFiles,
  invalidateSuiteRunSummaries,
  invalidateSuiteRuns,
  invalidateSuites,
  invalidateTestRunSummaries,
  invalidateTestRuns,
  invalidateTests,
  invalidateUploads,
  queryKeys,
} from './queryKeys';
import type { QueryFilters } from '@tanstack/react-query';

type BuildUploadResultsInvalidationsArgs = {
  selectedOrganisationId: string | null;
  selectedProjectId: string | null;
  uploadProjectId?: string | null;
};

type BuildUploadMutationInvalidationsArgs = BuildUploadResultsInvalidationsArgs & {
  purpose?: 'coverage' | 'results' | null;
};

const buildProjectIds = ({
  selectedProjectId,
  uploadProjectId,
}: Pick<
  BuildUploadResultsInvalidationsArgs,
  'selectedProjectId' | 'uploadProjectId'
>): string[] => {
  const projectIds = [selectedProjectId, uploadProjectId];
  return Array.from(
    new Set(
      projectIds.filter(
        (projectId): projectId is string => typeof projectId === 'string' && projectId.length > 0,
      ),
    ),
  );
};

const invalidateExecutionRunsProjectTotal = (
  organisationId: string | null,
  projectId: string,
): QueryFilters => ({
  queryKey: queryKeys.dataGrid(gridPaths.executionRuns, 'project-total', organisationId, projectId),
});

export const buildUploadResultsInvalidations = ({
  selectedOrganisationId,
  selectedProjectId,
  uploadProjectId = null,
}: BuildUploadResultsInvalidationsArgs): QueryFilters[] => {
  const organisationId = selectedOrganisationId ?? null;
  const projectIds = buildProjectIds({ selectedProjectId, uploadProjectId });
  const invalidations: QueryFilters[] = [
    invalidateUploads(organisationId, selectedProjectId),
    invalidateResultsFiles(organisationId, selectedProjectId),
    invalidateExecutionRuns(organisationId, selectedProjectId),
    invalidateBuilds(selectedProjectId, organisationId),
    invalidateGridQuery(gridPaths.uploads),
    invalidateGridQuery(gridPaths.resultsFiles),
    invalidateGridQuery(gridPaths.executionRuns),
    invalidateGridQuery(gridPaths.suites),
    invalidateGridQuery(gridPaths.tests),
    invalidateGridQuery(gridPaths.suiteRuns),
    invalidateGridQuery(gridPaths.testRuns),
    invalidateGridQuery(gridPaths.builds),
    invalidateDataGridPathPrefix('suite-runs/'),
    invalidateDataGridPathPrefix('test-runs/'),
    invalidateExecutionRunSummaries(),
    invalidateSuiteRunSummaries(),
    invalidateTestRunSummaries(),
    { queryKey: queryKeys.suiteSummaryPrefix(), exact: false },
    { queryKey: queryKeys.testSummaryPrefix(), exact: false },
    invalidateMetricsPrefix(),
    invalidateMetricsBatchPrefix(),
    { queryKey: queryKeys.dataGrid('uploads/status-count'), exact: false },
  ];

  if (organisationId !== null) {
    invalidations.push({ queryKey: queryKeys.organisationEntitlements(organisationId) });
  }

  if (selectedProjectId === null) {
    invalidations.push(
      invalidateSuites(null, organisationId),
      invalidateTests(null, organisationId),
      invalidateSuiteRuns(null, organisationId),
      invalidateTestRuns(null, organisationId),
      { queryKey: queryKeys.suiteOptions(null, organisationId), exact: false },
      { queryKey: queryKeys.tagOptions(null, organisationId), exact: false },
    );
  }

  projectIds.forEach((projectId) => {
    invalidations.push(
      invalidateUploads(organisationId, projectId),
      invalidateResultsFiles(organisationId, projectId),
      invalidateExecutionRuns(organisationId, projectId),
      invalidateExecutionRunsProjectTotal(organisationId, projectId),
      invalidateBuilds(projectId, organisationId),
      invalidateSuites(projectId, organisationId),
      invalidateTests(projectId, organisationId),
      invalidateSuiteRuns(projectId, organisationId),
      invalidateTestRuns(projectId, organisationId),
      { queryKey: queryKeys.suiteOptions(projectId, organisationId), exact: false },
      { queryKey: queryKeys.tagOptions(projectId, organisationId), exact: false },
    );
  });

  return invalidations;
};

export const buildCoverageUploadInvalidations = ({
  selectedOrganisationId,
  selectedProjectId,
  uploadProjectId = null,
}: BuildUploadResultsInvalidationsArgs): QueryFilters[] => {
  const organisationId = selectedOrganisationId ?? null;
  const projectIds = buildProjectIds({ selectedProjectId, uploadProjectId });
  const invalidations: QueryFilters[] = [
    invalidateUploads(organisationId, selectedProjectId),
    { queryKey: queryKeys.coverageUploads(organisationId, selectedProjectId), exact: false },
    { queryKey: queryKeys.coverageRuns(organisationId, selectedProjectId), exact: false },
    { queryKey: queryKeys.coverageRunSummary(organisationId, selectedProjectId), exact: false },
    { queryKey: queryKeys.coverageFiles(organisationId, selectedProjectId), exact: false },
    invalidateGridQuery(gridPaths.uploads),
    invalidateGridQuery(gridPaths.coverageRuns),
    invalidateGridQuery(gridPaths.coverageFiles),
    invalidateGridQuery('coverage-runs/local-table'),
    invalidateGridQuery('coverage-files/local-table'),
  ];

  if (selectedProjectId === null) {
    invalidations.push(
      invalidateUploads(organisationId, null),
      { queryKey: queryKeys.coverageUploads(organisationId, null), exact: false },
      { queryKey: queryKeys.coverageRuns(organisationId, null), exact: false },
      { queryKey: queryKeys.coverageRunSummary(organisationId, null), exact: false },
      { queryKey: queryKeys.coverageFiles(organisationId, null), exact: false },
    );
  }

  projectIds.forEach((projectId) => {
    invalidations.push(
      invalidateUploads(organisationId, projectId),
      { queryKey: queryKeys.coverageUploads(organisationId, projectId), exact: false },
      { queryKey: queryKeys.coverageRuns(organisationId, projectId), exact: false },
      { queryKey: queryKeys.coverageRunSummary(organisationId, projectId), exact: false },
      { queryKey: queryKeys.coverageFiles(organisationId, projectId), exact: false },
    );
  });

  return invalidations;
};

export const buildUploadMutationInvalidations = ({
  selectedOrganisationId,
  selectedProjectId,
  uploadProjectId = null,
  purpose = null,
}: BuildUploadMutationInvalidationsArgs): QueryFilters[] => {
  const invalidations = buildUploadResultsInvalidations({
    selectedOrganisationId,
    selectedProjectId,
    uploadProjectId,
  });

  if (purpose === 'coverage') {
    invalidations.push(
      ...buildCoverageUploadInvalidations({
        selectedOrganisationId,
        selectedProjectId,
        uploadProjectId,
      }),
    );
  }

  return invalidations;
};
